import {
  useDialogListener as useDialogListenerBase,
  useDialogTrigger as useDialogTriggerBase,
  emitCustomEvent as emitCustomEventBase
} from '@satellite/composables/useEventBasedDialog';

import type { AppointmentDetails } from '@satellite/composables/useAppointmentPresenter';
import type { PresenterPlaceholderData } from '@satellite/composables/presenterPlaceholder';
import type {
  AppointmentStatus,
  BaseTaskCreateData,
  CheckTaskFormData,
  IAppointment,
  IAssetContainer,
  MoveTaskFormData,
  TaskViewData
} from '@nova/core';
import type {
  CustomFormDataEntry,
  CustomFormSubmitMeta,
  ITrigger
} from '@satellite/components/custom_forms/types';
import type { LoadTypeDetails } from '@satellite/composables/loadTypePresenterShared';
import type { WarehouseDetails } from '@satellite/composables/useWarehousesPresenter';
import type { OpenReadyForPickupNotificationDialogEvent } from '@satellite/components/elements/dialogs/ReadyForPickupNotificationDialogBase.vue';
import type {
  AppointmentEventHubPayload,
  CreateRecurringSeriesResult
} from '@/modules/appointments/composables/useAppointmentMutations';

export type LinkAppointmentVisitLinkedPayload = {
  appointmentId: string;
  assetVisitId: string;
};

export type LinkAppointmentContainerLinkedPayload = {
  appointment: AppointmentDetails;
  appointmentType: 'drop' | 'pickup';
  assetContainerId: string;
};

export type LinkAppointmentLinkedPayload =
  | LinkAppointmentVisitLinkedPayload
  | LinkAppointmentContainerLinkedPayload;

export function isLinkAppointmentContainerLinkedPayload(
  payload: LinkAppointmentLinkedPayload
): payload is LinkAppointmentContainerLinkedPayload {
  return 'appointment' in payload;
}

export function isLinkAppointmentVisitLinkedPayload(
  payload: LinkAppointmentLinkedPayload
): payload is LinkAppointmentVisitLinkedPayload {
  return 'appointmentId' in payload;
}

export type LinkTarget =
  | {
      kind: 'assetVisit';
      assetVisitId: string;
    }
  | {
      kind: 'assetContainer';
      assetContainerId: string;
      appointmentType: 'drop' | 'pickup';
      allowLinkToArrivedAppointments?: boolean;
    };

export type OpenLinkAppointmentDialogEvent = {
  appointmentDaysThreshold?: number;
  followToAppointmentDetails?: boolean;
  linkTarget: LinkTarget;
  onLinked?: (payload: LinkAppointmentLinkedPayload) => void;
};

export type OpenRemoveCarrierContactDialogEvent = {
  carrierContact: {
    id: string;
    isFavorite: boolean;
    emailCCs: string[];
    company?: {
      name: string;
    };
    user: {
      company?: {
        name: string;
      };
      firstName?: string;
      lastName?: string;
      fullName?: string;
    };
  };
  mixpanelData?: unknown;
  onContactRemoved?: () => void;
};

export type OpenCreateCarrierCompanyDialogEvent = {
  mixpanelEntryPoint: string;
  onCompanyCreated?: (company: unknown) => void;
};

export type OpenCreateCarrierContactDialogEvent = {
  mixpanelEntryPoint: string;
  onContactCreated?: (contact: unknown) => void;
};

/**
 * Payload for the create-load-type dialog
 * (`EventTriggeredCreateLoadTypeDialog`).
 */
export type OpenCreateLoadTypeDialogEvent = {
  /** Warehouse the load type is created under. */
  warehouseId: string;
  mixpanelEntryPoint: string;
  onLoadTypeCreated?: (loadType: LoadTypeDetails) => void;
};

export type OpenCreateYardTaskDialogEvent = BaseTaskCreateData & {
  warehouseId?: string;
  warehouseTimezone?: string;
  mixpanelEntryPoint?: string;
  onTaskCreated?: (task: unknown) => void;
};

export type OpenCreateYardMoveTaskDialogEvent = OpenCreateYardTaskDialogEvent &
  MoveTaskFormData & {
    onSkipCreateTask?: () => void;
    /** Whether Skip and confirm move should render. Independent of `onSkipCreateTask` being set. */
    allowSkipCreateTask?: boolean;
  };

export type OpenCreateYardCheckTaskDialogEvent = OpenCreateYardTaskDialogEvent & CheckTaskFormData;

export type OpenEditYardTaskDialogEvent = {
  taskId: string;
  warehouseTimezone: string;
  mixpanelEntryPoint?: string;
  onTaskUpdated?: (task: unknown) => void;
};

/** The id is the whole contract: the drawer fetches the visit and the warehouse itself. */
export type OpenDriversChatEvent = {
  assetVisitId: string;
};

/** The drawer shows the selected warehouse, so it needs no id. */
export type OpenArrivalsDrawerEvent = {
  /** Close the drawer instead when it is already open, like the top nav bell does. */
  toggle?: boolean;
  /** Runs once the drawer closes, so the opener can mark what was on screen as seen. */
  onClose?: () => void;
};

export type OpenYardTaskConfirmationDialogEvent = {
  task: TaskViewData;
  mixpanelEntryPoint?: string;
};

export type OpenYardTaskCancelConfirmationDialogEvent = OpenYardTaskConfirmationDialogEvent;
export type OpenYardTaskCompleteConfirmationDialogEvent = OpenYardTaskConfirmationDialogEvent;
export type OpenYardTaskStartConfirmationDialogEvent = OpenYardTaskConfirmationDialogEvent;

export type OpenGateFeedImageCarouselEvent = {
  images: string[];
};

export type OpenGateFeedAddAssetDialogEvent = {
  vehicleObservation: unknown;
};

export type OpenLimitsOverviewDrawerEvent = {
  dateMode: 'day' | 'week' | 'month';
  baseDate: string; // ISO date string
  warehouseId: string;
  timezone: string;
  isMilitaryTimeEnabled: boolean;
};

/**
 * The five tab keys of the greenfield edit-appointment dialog (details-v2).
 * Kept as a literal union so callers can deep-link straight into a tab.
 */
export type EditAppointmentDialogTab =
  | 'details'
  | 'claims-support'
  | 'gate-management'
  | 'drivers-chat'
  | 'audit-log';

export type OpenEditAppointmentDialogEvent = {
  appointmentId: string;
  placeholderAppointmentData?: PresenterPlaceholderData<AppointmentDetails>;
  initialTab?: EditAppointmentDialogTab;
};

/** The tab keys of the edit-reserve dialog (reserve-v2), so callers can deep-link a tab. */
export type EditReserveDialogTab = 'details' | 'audit-log';

/**
 * Payload for `EventTriggeredEditReserveDialog`. Like the edit-appointment dialog it takes
 * an id and reads the reserve itself; the caller's row only paints the dialog early.
 */
export type OpenEditReserveDialogEvent = {
  appointmentId: string;
  placeholderAppointmentData?: PresenterPlaceholderData<AppointmentDetails>;
  initialTab?: EditReserveDialogTab;
};

/**
 * Values the create-appointment form starts from. Every field is optional: the dialog
 * pre-selects what it is given and leaves the user on the first incomplete step.
 */
export type CreateAppointmentSeed = {
  warehouseId?: string;
  /** Docks to pre-select. Capacity children resolve to their parent, which is what the select lists. */
  dockIds?: string[];
  loadTypeId?: string;
  /** Carrier *contact* (user) id, not the company. */
  carrierId?: string;
  /** ISO timestamp in the warehouse timezone; pre-selects the date and the slot. */
  startISO?: string;
  units?: number | null;
};

/**
 * Payload for `EventTriggeredCreateAppointmentDialog`. The modes only change how the form is
 * seeded; `clone` and `fromAssetVisit` pass an id the dialog reads once on open.
 */
export type OpenCreateAppointmentDialogEvent = (
  | { mode?: 'blank'; seed?: CreateAppointmentSeed }
  | { mode: 'clone'; appointmentId: string }
  | { mode: 'fromAssetVisit'; assetVisitId: string; seed?: CreateAppointmentSeed }
) & {
  /** Opens the edit dialog on the new appointment once it is created. Defaults to true. */
  followToAppointmentDetails?: boolean;
  mixpanelEntryPoint?: string;
  onCreated?: (appointment: AppointmentDetails) => void;
};

/**
 * Payload for `EventTriggeredRescheduleAppointmentDialog`. Like the edit dialog, it takes
 * an id and reads the appointment itself, so no caller has to hold a joined row.
 */
export type OpenRescheduleAppointmentDialogEvent = {
  appointmentId: string;
  placeholderAppointmentData?: PresenterPlaceholderData<AppointmentDetails>;
  mixpanelEntryPoint?: string;
  onRescheduled?: (appointment: AppointmentDetails) => void;
};

/**
 * Payload for `EventTriggeredCreateAppointmentRecurrenceDialog`. Takes an id like the
 * reschedule dialog; the caller's row, when it has one, only paints the dialog early.
 */
export type OpenCreateAppointmentRecurrenceDialogEvent = {
  appointmentId: string;
  placeholderAppointmentData?: PresenterPlaceholderData<AppointmentDetails>;
  /** Called once the series is created, including a partial one that listed failures. */
  onRecurrenceCreated?: (result: CreateRecurringSeriesResult) => void;
};

/** What a recurrence delete did, so the caller can react to its own row being gone. */
export type DeleteAppointmentRecurrenceOutcome = {
  /** The appointment the series was created from; the delete never removes it. */
  parentAppointment: IAppointment;
  /** True when the dialog was opened on a child, which the delete removed along with the rest. */
  isOpenedAppointmentDeleted: boolean;
};

/**
 * Payload for `EventTriggeredDeleteAppointmentRecurrenceDialog`. Takes an id like the create
 * dialog; the appointment must carry `recurringParent`, which the shared fetcher joins.
 */
export type OpenDeleteAppointmentRecurrenceDialogEvent = {
  appointmentId: string;
  placeholderAppointmentData?: PresenterPlaceholderData<AppointmentDetails>;
  onRecurrenceDeleted?: (outcome: DeleteAppointmentRecurrenceOutcome) => void;
};

export type OpenEditAppointmentCustomFieldsDialogEvent = {
  appointment: AppointmentDetails;
  warehouse: WarehouseDetails;
};

/**
 * Payload for the yard check-in / add-to-yard dialog
 * (`EventTriggeredYardViewAssetCreateDialog`).
 * Callers gate on YMS before triggering.
 */
export type OpenYardViewAssetCreateDialogEvent = {
  warehouseId: string;
  refNumberDisplayText?: string;
  addContainerOnly?: boolean;
  assetVisitId?: string;
  initialAssetType?: string | null;
  linkedAppointment?: IAppointment | null;
  onContainerCreated?: (container: Partial<IAssetContainer>) => void;
};

export type UnlinkCheckinOutcome = 'unlinked' | 'kept' | 'dismissed';

/**
 * Payload for the Arrived unlink/keep confirm
 * (`EventTriggeredUnlinkCheckinDialog`). Passes the appointment EventHub
 * payload (not just an id) so unlink can fan out without a refetch.
 */
export type OpenUnlinkCheckinDialogEvent = {
  appointment: AppointmentEventHubPayload;
  assetVisitId: string;
  onResolved?: (outcome: UnlinkCheckinOutcome) => void;
};

/**
 * Payload for EventTriggeredEditStatusTimelineDialog. The caller's appointment row
 * serves two purposes inside the dialog: it supplies the id to fetch by, and it is
 * handed to the fetcher as placeholder data so the form renders immediately.
 */
export type OpenEditStatusTimelineDialogEvent = {
  appointment: AppointmentDetails;
  statusToEdit: AppointmentStatus;
  timezone: string;
  militaryTimeEnabled?: boolean;
  /** Present so the mutation invalidates the visit query too, per the freshness rule. */
  assetVisitId?: string;
  /** Mixpanel "Entry Point" of the surface that opened the dialog. */
  mixpanelEntryPoint?: string;
};

/**
 * create may emit a field array or `{ skip: true }` from the Skip action.
 * Callers must forward the value unchanged — do not narrow to an array alone.
 */
export type EditCustomFormResult = CustomFormDataEntry[] | { skip?: boolean };

/**
 * How an opener handles a form whose Action fields pick a feature (for example, check-out
 * choosing Inbound Claim):
 * - `chain`: its `onCreate` / `onUpdate` read `meta.requestedFeatures` and return the next form.
 * - `legacy-event-hub`: it relies on the deprecated `action-field-submitted` broadcast, which
 *   only the legacy AppointmentDetails and mobile StatusTimelineMobile listen to.
 *
 * Required so every opener states it. Leaving it out is a type error, not a feature that
 * silently does nothing.
 */
export type CustomFormActionFieldsHandling = 'chain' | 'legacy-event-hub';

/**
 * A form to show next, in place of the one just submitted. It cannot gate a status change, so a
 * chained form always saves through the plain custom-form-data write path.
 */
export type ChainedCustomForm = Omit<OpenEditCustomFormDialogEvent, 'gatedStatusChange'>;

/**
 * Payload for the custom form dialog (`EventTriggeredEditCustomFormDialog`).
 * Unlike the unlink dialog, this one owns no mutations: it is a form host, so
 * the caller keeps persistence and receives the submitted entries back.
 *
 * `onCreate` and `onUpdate` may return the next form to show. The dialog then swaps it in
 * without closing; returning nothing closes the dialog. The next form opens only after the
 * callback resolves, so only after the submitted form has been saved; a rejected callback keeps
 * the dialog open and chains nothing.
 */
export type OpenEditCustomFormDialogEvent = {
  trigger: ITrigger;
  objectId: string;
  warehouse: WarehouseDetails;
  /**
   * Set only when the form is gating a status transition. Presence — not the
   * value — switches the form into gate mode: it skips loading prior answers
   * (so submit emits `create`, never `update`), renders the Skip action, and
   * leaves closing to the caller. Omit it to open the form as a plain editor
   * over existing answers.
   */
  gatedStatusChange?: AppointmentStatus;
  actionFields: CustomFormActionFieldsHandling;
  /** `meta` is absent for Skip, which chooses no features. */
  onCreate?: (
    result: EditCustomFormResult,
    meta?: CustomFormSubmitMeta
  ) => ChainedCustomForm | void | Promise<ChainedCustomForm | void>;
  /** Update emits field rows only — Skip is a create-path concern. */
  onUpdate?: (
    result: CustomFormDataEntry[],
    meta?: CustomFormSubmitMeta
  ) => ChainedCustomForm | void | Promise<ChainedCustomForm | void>;
  onClose?: () => void;
};

export type { OpenReadyForPickupNotificationDialogEvent };

export type CustomEventMap = {
  /**
   * list here all the custom events that are used in the app
   * so the IDE's type inference helps us when emitting the events.
   */
  'open-remove-carrier-contact-dialog': CustomEvent<OpenRemoveCarrierContactDialogEvent>;
  'open-create-carrier-company-dialog': CustomEvent<OpenCreateCarrierCompanyDialogEvent>;
  'open-create-carrier-contact-dialog': CustomEvent<OpenCreateCarrierContactDialogEvent>;
  'open-create-load-type-dialog': CustomEvent<OpenCreateLoadTypeDialogEvent>;
  'open-create-yard-task-drawer': CustomEvent<
    OpenCreateYardMoveTaskDialogEvent | OpenCreateYardCheckTaskDialogEvent
  >;
  'open-create-yard-move-task-drawer': CustomEvent<OpenCreateYardMoveTaskDialogEvent>;
  'open-gate-feed-image-carousel': CustomEvent<OpenGateFeedImageCarouselEvent>;
  'open-gate-feed-add-asset-dialog': CustomEvent<OpenGateFeedAddAssetDialogEvent>;
  'open-edit-yard-task-drawer': CustomEvent<OpenEditYardTaskDialogEvent>;
  'open-drivers-chat': CustomEvent<OpenDriversChatEvent>;
  'open-arrivals-drawer': CustomEvent<OpenArrivalsDrawerEvent>;
  'open-yard-task-cancel-confirmation-dialog': CustomEvent<OpenYardTaskCancelConfirmationDialogEvent>;
  'open-yard-task-complete-confirmation-dialog': CustomEvent<OpenYardTaskCompleteConfirmationDialogEvent>;
  'open-yard-task-start-confirmation-dialog': CustomEvent<OpenYardTaskStartConfirmationDialogEvent>;
  'open-limits-overview-drawer': CustomEvent<OpenLimitsOverviewDrawerEvent>;
  'open-ready-for-pickup-notification-dialog': CustomEvent<OpenReadyForPickupNotificationDialogEvent>;
  'open-link-appointment-dialog': CustomEvent<OpenLinkAppointmentDialogEvent>;
  'open-create-appointment-dialog': CustomEvent<OpenCreateAppointmentDialogEvent>;
  'open-reschedule-appointment-dialog': CustomEvent<OpenRescheduleAppointmentDialogEvent>;
  'open-create-appointment-recurrence-dialog': CustomEvent<OpenCreateAppointmentRecurrenceDialogEvent>;
  'open-delete-appointment-recurrence-dialog': CustomEvent<OpenDeleteAppointmentRecurrenceDialogEvent>;
  'open-edit-appointment-dialog': CustomEvent<OpenEditAppointmentDialogEvent>;
  'open-edit-reserve-dialog': CustomEvent<OpenEditReserveDialogEvent>;
  'open-edit-appointment-custom-fields-dialog': CustomEvent<OpenEditAppointmentCustomFieldsDialogEvent>;
  'open-yard-view-asset-create-dialog': CustomEvent<OpenYardViewAssetCreateDialogEvent>;
  'open-unlink-checkin-dialog': CustomEvent<OpenUnlinkCheckinDialogEvent>;
  'open-edit-status-timeline-dialog': CustomEvent<OpenEditStatusTimelineDialogEvent>;
  'open-edit-custom-form-dialog': CustomEvent<OpenEditCustomFormDialogEvent>;
};

/**
 * Composable for listening to dialog events (used in dialog components)
 */
export function useDialogListener<K extends keyof CustomEventMap>(dialogName: K) {
  return useDialogListenerBase<CustomEventMap, K>(dialogName);
}

/**
 * Composable for triggering dialog events (used in components that open dialogs)
 */
export function useDialogTrigger<K extends keyof CustomEventMap>(dialogName: K) {
  return useDialogTriggerBase<CustomEventMap, K>(dialogName);
}

/**
 * Emit a custom event (overload without data)
 */
export function emitCustomEvent<K extends keyof CustomEventMap>(
  eventName: K,
  target: EventTarget
): void;

/**
 * Emit a custom event (overload with data)
 */
export function emitCustomEvent<K extends keyof CustomEventMap>(
  eventName: K,
  data: CustomEventMap[K]['detail'],
  target?: EventTarget
): void;

/**
 * Emit a custom event
 */
export function emitCustomEvent<K extends keyof CustomEventMap>(
  eventName: K,
  dataOrTarget?: CustomEventMap[K]['detail'] | EventTarget,
  maybeTarget?: EventTarget
): void {
  emitCustomEventBase<CustomEventMap, K>(eventName, dataOrTarget, maybeTarget);
}

declare global {
  interface DocumentEventMap extends CustomEventMap {}
}
